import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from '@/stores/chatStore';
import { Send, Loader2 } from 'lucide-react';

type PageContext = 'monitor' | 'change' | 'automate' | 'setup' | 'browser' | 'report';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface ChatSidebarProps {
  context: PageContext;
}

const SUGGESTED_ACTIONS: Record<PageContext, string[]> = {
  monitor: [
    'Show me call quality trends for the past week',
    'Which features have the lowest adoption?',
    'Summarize recent stakeholder feedback',
    'Alert me when queue wait time exceeds 5 minutes',
    'Generate a weekly quality report for leadership',
  ],
  change: [
    'What changes were made last week?',
    'Show me all failed changes',
    'Rollback the most recent change',
    'Schedule a change for after business hours',
  ],
  automate: [
    'Create a Slack alert for call quality issues',
    'Automate new hire onboarding for Support',
    'Send weekly adoption reports to leadership',
    'Create a ServiceNow ticket when queues overflow',
    'Notify me when AI Companion usage drops',
  ],
  setup: [
    'What requirements are still pending?',
    'Show me failed verifications',
    'Re-run verification for Voice Queues',
    'Export grading report as markdown',
    'Help me fix the routing configuration',
  ],
  browser: [
    'Navigate to Zoom settings',
    'Find the recording toggle',
    'Click the save button',
    'Show me all form fields on this page',
    'What controls are available here?',
  ],
  report: [
    'Summarize this account overview',
    'What security risks do you see?',
    'How does usage compare to the plan?',
    'What optimizations do you recommend?',
    'Generate a report for leadership',
  ],
};

const CONTEXT_GREETINGS: Record<PageContext, string> = {
  monitor: "I can help you track metrics, set up alerts, and generate reports. What would you like to monitor?",
  change: "I can help you review changes, schedule new ones, or troubleshoot issues. What do you need?",
  automate: "I can build automations for you. Describe what you want to happen and when, and I'll set it up.",
  setup: "I can help you configure Zoom from requirements. Upload a document or ask me about specific configurations.",
  browser: "I can help you control the browser. Tell me what to click, navigate to, or configure on the current page.",
  report: "I can help you analyze this account's data. Ask me about security, usage patterns, or recommendations.",
};

export function ChatSidebar({ context }: ChatSidebarProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const createConversation = useChatStore((s) => s.createConversation);
  const storeSendMessage = useChatStore((s) => s.sendMessage);

  // Reset messages when context changes
  useEffect(() => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: CONTEXT_GREETINGS[context],
      },
    ]);
    setShowSuggestions(true);
  }, [context]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    void sendMessage(input.trim());
  };

  const sendMessage = async (content: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setShowSuggestions(false);

    // Hand off to the real chat: new conversation, send, and jump to /chat
    // where the full streaming UI takes over.
    try {
      await createConversation();
      void storeSendMessage(content);
      navigate('/chat');
    } catch {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    void sendMessage(suggestion);
  };

  const suggestions = SUGGESTED_ACTIONS[context];

  return (
    <aside className="w-80 bg-card border-l border-border flex flex-col h-screen sticky top-0">
      {/* Header */}
      <div className="h-14 flex items-center px-4 border-b border-border">
        <span className="text-sm font-medium text-foreground">Chat with Zac</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map(message => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-foreground'
              }`}
            >
              {message.content}
            </div>
          </div>
        ))}

        {/* Suggested Actions */}
        {showSuggestions && !isLoading && (
          <div className="space-y-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-3 py-2 text-sm text-muted-foreground bg-secondary/50 hover:bg-secondary hover:text-foreground rounded-lg transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-secondary rounded-lg px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-border">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask Zac anything..."
            className="flex-1 bg-secondary border-0 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </aside>
  );
}
